import { Check } from "lucide-react";
import { Fragment } from "react";
import { cn } from "@/lib/utils";

/**
 * The setup wizard's pill rail, one pill a step.
 *
 * Three states, as the design draws them: completed is a filled black pill with
 * a check, current is brand orange, and future is an outlined grey. The
 * connector line is black up to the current step and grey after it, so the rail
 * reads as a progress bar even at a glance.
 *
 * Accessibility is doing real work here and is not decoration:
 *
 *   - It is a `nav` with `aria-current="step"` on the active pill, so a screen
 *     reader user can find "where am I" without inferring it from colour.
 *   - Each pill carries a visually-hidden "Step 3 of 4, Compliance", because the
 *     visible label is a bare numeral and the caption beneath is a separate
 *     element that would not be announced with it.
 *   - Pills are `<span>`s, not disabled buttons, so they are skipped by tab
 *     order rather than being stops that do nothing.
 *
 * `onSelect` is optional and the setup wizard no longer passes it, which makes
 * the rail a progress indicator rather than a control. Jumping between steps
 * used to lose the page you were leaving: the step component unmounted and its
 * `useState` went with it, because navigation is a `navigate()` call and only
 * Continue commits. A rail that silently discards an edit is worse than one you
 * cannot click, so the affordance goes rather than the state being rescued —
 * see `wizard.tsx`. Kept as a prop because the rail is a general component and
 * a caller that *does* commit on transition may legitimately want it.
 */
export type StepperStep = {
  /** 1-based. */
  number: number;
  label: string;
};

export function Stepper({
  steps,
  current,
  /** The furthest step reached — everything up to it is navigable. */
  furthest,
  onSelect,
  className,
}: {
  steps: StepperStep[];
  current: number;
  furthest: number;
  onSelect?: (step: number) => void;
  className?: string;
}) {
  return (
    <nav aria-label="Project setup progress" className={cn("w-full", className)}>
      {/* Circles with the connectors between them. The design captions the
          current step once beneath the rail rather than labelling every pill,
          so the label here is carried by the visually-hidden text only. */}
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const isCurrent = step.number === current;
          const isComplete = step.number < current;
          const reachable = step.number <= furthest && !isCurrent;
          const isLast = index === steps.length - 1;

          const pill = (
            <>
              <span aria-hidden>
                {isComplete ? <Check size={16} strokeWidth={3} /> : step.number}
              </span>
              <span className="sr-only">
                Step {step.number} of {steps.length}, {step.label}
                {isCurrent ? " (current)" : isComplete ? " (completed)" : ""}
              </span>
            </>
          );

          const clickable = reachable && Boolean(onSelect);

          const pillClass = cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full",
            "font-display text-[0.92rem] font-bold transition-colors",
            isCurrent
              ? "bg-brand text-white shadow-brand"
              : isComplete
                ? "bg-chrome text-white"
                : "border-[1.5px] border-border bg-surface text-faint",
            // Only when something happens on click. A pointer cursor over a
            // pill that does nothing is a promise the rail cannot keep.
            clickable && "cursor-pointer hover:bg-ink-soft",
          );

          return (
            <Fragment key={step.number}>
              <li className="flex shrink-0 items-center">
                {clickable && onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(step.number)}
                    aria-current={isCurrent ? "step" : undefined}
                    className={pillClass}
                  >
                    {pill}
                  </button>
                ) : (
                  <span aria-current={isCurrent ? "step" : undefined} className={pillClass}>
                    {pill}
                  </span>
                )}
              </li>
              {!isLast && (
                <li
                  aria-hidden
                  className={cn(
                    "h-[2px] min-w-4 flex-1 rounded-full",
                    // Black up to the current step, grey beyond it.
                    step.number < current ? "bg-chrome" : "bg-border",
                  )}
                />
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
